import { TabBar } from '@components/TabBar';
import { TabControl } from '@components/TabControl';
import { TabPage } from '@components/TagPage';
import { useData } from '@hooks/useData';
import { useEffectOnce } from '@hooks/useEffectOnce';
import { useField } from '@hooks/useField';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { useTab } from '@hooks/useTab';
import { makeStyles, Tab } from '@material-ui/core';
import { ICliente } from '@model/Cliente';
import { IClienteEvento } from '@model/ClienteEvento';
import { EnumClienteEventoStatus, useClienteEventoStatusCombo } from '@model/ClienteEventoStatus';
import { EnumClienteEventoTipo, useClienteEventoTipoCombo } from '@model/ClienteEventoTipo';
import { IClienteSaldo } from '@model/ClienteSaldo';
import { IContratoCliente, useOrigemCancelamentoCombo } from '@model/ContratoCliente';
import { EnumContratoClienteStatus, useContratoClienteStatusCombo } from '@model/ContratoClienteStatus';
import { useEmpresaCombo } from '@model/Empresa';
import IFatura from '@model/Fatura';
import { useFaturaStatusCombo, EnumFaturaStatus } from '@model/FaturaStatus';
import { useClienteService, IFaturaFilter, IClienteEventoFilter, IContratoClienteFilter } from '@services/ClienteService';
import React from 'react';
import { ClienteDetalhesContratos } from './Contratos';
import { ClienteDetalhesEventos } from './Eventos';
import { ClienteDetalhesFaturas } from './Faturas';
import { ClienteDetalhesSaldos } from './Saldos';

enum EnumPages {
    Faturas = 0,
    Contratos = 1,
    Eventos = 2,
    CreditosDebitos = 3,
}

export type ClienteTabProps = {
    cliente: ICliente
}

export function ClienteTab ({ cliente }: ClienteTabProps) {
    const cls = useStyles();

    const tab = useTab();

    const faturaLoading = useLoading();
    const contratoLoading = useLoading();
    const eventoLoading = useLoading();
    const saldoLoading = useLoading();

    const message = useMessage();
    const clienteService = useClienteService();

    const empresaCombo = useEmpresaCombo();
    const faturaStatusCombo = useFaturaStatusCombo();
    const clienteEventoStatusCombo = useClienteEventoStatusCombo();
    const clienteEventoTipoCombo = useClienteEventoTipoCombo();
    const contratoClienteStatusCombo = useContratoClienteStatusCombo();
    const origemCancelamentoCombo = useOrigemCancelamentoCombo();

    const faturaData = useData<IFatura, IFaturaFilter>({
        filter: {
            faturaStatus: useField(EnumFaturaStatus.Aberta)
        }
    })

    const contratoData = useData<IContratoCliente, IContratoClienteFilter>({
        filter: {
            contratoClienteStatus: useField(EnumContratoClienteStatus.Ativo)
        }
    })

    const eventoData = useData<IClienteEvento, IClienteEventoFilter>({
        filter: {
            clienteEventoTipo: useField(EnumClienteEventoStatus.None),
            clienteEventoStatus: useField(EnumClienteEventoTipo.None)
        }
    })

    const saldoData = useData<IClienteSaldo>()

    const listarFaturas = React.useCallback(() => {
        if (!faturaLoading.isLoading) {
            const filterValues = faturaData.filter.getInputValues();

            faturaLoading.start();
            clienteService.listarFaturas(cliente.clienteId, filterValues)
                .then(faturaData.handleDataResponse)
                .catch(message.showException)
                .finally(faturaLoading.stop);
        }
    }, [cliente.clienteId, clienteService, faturaData.filter, faturaData.handleDataResponse, faturaLoading, message.showException]);

    const listarContratos = React.useCallback(() => {
        if (!contratoLoading.isLoading) {
            const filterValues = contratoData.filter.getInputValues();

            contratoLoading.start();
            clienteService.listarContratos(cliente.clienteId, filterValues)
                .then(contratoData.handleDataResponse)
                .catch(message.showException)
                .finally(contratoLoading.stop);
        }
    }, [cliente.clienteId, clienteService, contratoData.filter, contratoData.handleDataResponse, contratoLoading, message.showException]);

    const listarEventos = React.useCallback(() => {
        if (!eventoLoading.isLoading) {
            const filterValues = eventoData.filter.getInputValues();

            eventoLoading.start();
            clienteService.listarEventos(cliente.clienteId, filterValues)
                .then(eventoData.handleDataResponse)
                .catch(message.showException)
                .finally(eventoLoading.stop);
        }
    }, [cliente.clienteId, clienteService, eventoData.filter, eventoData.handleDataResponse, eventoLoading, message.showException]);

    const listarSaldos = React.useCallback(() => {
        if (!saldoLoading.isLoading) {
            saldoLoading.start();
            clienteService.listarSaldos(cliente.clienteId)
                .then(saldoData.handleDataResponse)
                .catch(message.showException)
                .finally(saldoLoading.stop);
        }
    }, [cliente.clienteId, clienteService, message.showException, saldoData.handleDataResponse, saldoLoading]);

    React.useEffect(() => {
        listarFaturas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [faturaData.filter.requestedFilter]);

    React.useEffect(() => {
        listarEventos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventoData.filter.requestedFilter]);

    React.useEffect(() => {
        listarContratos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contratoData.filter.requestedFilter]);

    React.useEffect(() => {
        listarSaldos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saldoData.filter.requestedFilter]);

    useEffectOnce(() => {
        empresaCombo.loadOptions();

        faturaStatusCombo.loadOptions();

        contratoClienteStatusCombo.loadOptions();

        clienteEventoTipoCombo.loadOptions();
        clienteEventoStatusCombo.loadOptions();
        origemCancelamentoCombo.loadOptions();

        listarSaldos();
    });

    return (
        <React.Fragment>
            <TabBar className={cls.root}>
                <TabControl tab={tab}>
                    <Tab label="Faturas" value={EnumPages.Faturas}></Tab>
                    <Tab label="Contratos" value={EnumPages.Contratos}></Tab>
                    <Tab label="Eventos" value={EnumPages.Eventos}></Tab>
                    <Tab label="Créditos / Débitos" value={EnumPages.CreditosDebitos}></Tab>
                </TabControl>
            </TabBar>
        
            <TabPage index={EnumPages.Faturas} tabIndex={tab.tabIndex}>
                <ClienteDetalhesFaturas
                    cliente={cliente}
                    data={faturaData}
                    faturaStatusCombo={faturaStatusCombo}
                    empresaCombo={empresaCombo}
                    isLoading={faturaLoading.isLoading}
                />            
            </TabPage>

            <TabPage index={EnumPages.Contratos} tabIndex={tab.tabIndex}>
                <ClienteDetalhesContratos
                    data={contratoData}
                    contratoClienteStatusCombo={contratoClienteStatusCombo}
                    origemCancelamentoCombo={origemCancelamentoCombo}
                    isLoading={contratoLoading.isLoading}
                />            
            </TabPage>

            <TabPage index={EnumPages.Eventos} tabIndex={tab.tabIndex}>
                <ClienteDetalhesEventos
                    data={eventoData}
                    cliente={cliente}
                    clienteEventoStatusCombo={clienteEventoStatusCombo}
                    clienteEventoTipoCombo={clienteEventoTipoCombo}
                    isLoading={eventoLoading.isLoading}
                />            
            </TabPage>

            <TabPage index={EnumPages.CreditosDebitos} tabIndex={tab.tabIndex}>
                <ClienteDetalhesSaldos
                    data={saldoData}
                    isLoading={saldoLoading.isLoading}
                    cliente={cliente}
                    empresaCombo={empresaCombo}
                />            
            </TabPage>
        </React.Fragment>
    )
}

const useStyles = makeStyles(theme => ({
    root: {
        marginTop: theme.spacing(2)
    }
}))